import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useSuspenseQuery } from "@apollo/client/react";
import {
  GetAreaRecordGeoItemsDocument,
  type GetAreaRecordGeoItemsQuery,
  type GetAreaRecordGeoItemsQueryVariables,
} from "~/features/recordGeoItems/gqls";
import { useTenant } from "~/features/tenant/context";

type AreaRecordGeoItemsResponse = GetAreaRecordGeoItemsQuery["cnutGetRecordGeoItemsByRecord"];
type AreaRecordGeoItems = Extract<AreaRecordGeoItemsResponse, { __typename?: "RecordGeoItemsByRecordOutput" }>;

export function requireAreaRecordGeoItems(response: AreaRecordGeoItemsResponse): AreaRecordGeoItems {
  if (response.__typename === "ValidationError") throw new Error(response.message);
  return {
    ...response,
    recordGeoItems: [...response.recordGeoItems].sort((a, b) => {
      if (a.date == null) return b.date == null ? a.geoItemId.localeCompare(b.geoItemId) : 1;
      if (b.date == null) return -1;
      if (a.date === b.date) return a.geoItemId.localeCompare(b.geoItemId);
      return a.date < b.date ? -1 : 1;
    }),
  };
}

/** A record's geo-items (for the area list sidebar). */
export function useAreaRecordGeoItems(recordId: string) {
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");
  const result = useSuspenseQuery<GetAreaRecordGeoItemsQuery, GetAreaRecordGeoItemsQueryVariables>(
    GetAreaRecordGeoItemsDocument,
    { variables: { tenantId, recordId }, errorPolicy: "all" },
  );

  if (result.error) {
    const isNotFound =
      CombinedGraphQLErrors.is(result.error) &&
      result.error.errors.some(({ extensions }) => extensions?.status === 404);
    if (isNotFound) return { ...result, data: { records: [] } };
    throw result.error;
  }
  if (!result.data) throw new Error("Area record GeoItems data is required");

  return {
    ...result,
    data: { records: [requireAreaRecordGeoItems(result.data.cnutGetRecordGeoItemsByRecord)] },
  };
}
